import {CheckIcon} from '@primer/octicons-react'
import {ActionList, Label} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import React from 'react'

import {HighlightedText} from './HighlightedText'
import {useNavigate} from '@github-ui/use-navigate'

import styles from './RefItem.module.css'

interface RefItemProps {
  href?: string
  isCurrent?: boolean
  isDefault?: boolean
  gitRef: string
  filterText: string
  ariaPosInSet?: number
  ariaSetSize?: number
  onSelect?: (gitRef: string) => void
  onClick?: (gitRef: string) => void
  shouldSetAsDiv?: boolean
}

export const RefItem = React.memo(function RefItemInner({
  isCurrent,
  isDefault,
  href,
  gitRef,
  filterText,
  ariaPosInSet,
  ariaSetSize,
  onSelect,
  onClick,
  shouldSetAsDiv = false,
}: RefItemProps) {
  const innerRefItem = (
    <RefItemContent gitRef={gitRef} isDefault={isDefault} isCurrent={isCurrent} filterText={filterText} />
  )

  const commonProps = {
    'aria-posinset': shouldSetAsDiv ? undefined : ariaPosInSet,
    'aria-setsize': shouldSetAsDiv ? undefined : ariaSetSize,
    sx: {minWidth: 0},
    onSelect: () => {
      onSelect?.(gitRef)
      if (href) {
        navigate(href, {reloadDocument: true})
      }
    },
    onClick: () => {
      onClick?.(gitRef)
      if (href) {
        navigate(href, {reloadDocument: true})
      }
    },
  }

  const navigate = useNavigate()

  return shouldSetAsDiv ? (
    <ActionList.Item role="menuitemradio" as="div" {...commonProps}>
      {innerRefItem}
    </ActionList.Item>
  ) : (
    <ActionList.Item role="menuitemradio" {...commonProps}>
      {innerRefItem}
    </ActionList.Item>
  )
})

interface RefItemContentProps extends RefItemProps {
  showLeadingVisual?: boolean
}

export const RefItemContent = React.memo(function RefItemInner({
  isCurrent,
  isDefault,
  gitRef,
  filterText,
  showLeadingVisual = true,
}: RefItemContentProps) {
  return (
    <div style={{display: 'flex', justifyContent: 'space-between'}}>
      <div style={{display: 'flex', minWidth: 0, alignItems: 'flex-end'}}>
        {showLeadingVisual && (
          <Octicon
            icon={CheckIcon}
            aria-hidden
            data-icon-visibility={isCurrent ? 'visible' : 'hidden'}
            className={styles.ItemIcon}
          />
        )}
        <HighlightedText key={gitRef} hideOverflow search={filterText} text={gitRef} />
      </div>
      {isDefault && <Label>default</Label>}
    </div>
  )
})

try{ RefItem.displayName ||= 'RefItem' } catch {}
try{ RefItemContent.displayName ||= 'RefItemContent' } catch {}